/* eslint-disable no-console */
import { AnalyticsBrowser } from '@segment/analytics-next';
import Cookies from 'js-cookie';
import * as Sentry from '@sentry/vue';
import { ENV, isDevelopment, isTesting } from '../../config/app-env';
import { ENVIRONMENTS } from '../data/environments';
import { SEGMENT_EVENTS } from '@/data/segment-events';
import { STATSIG_EVENTS } from '@/data/statsig-events';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import { logEvent } from './init-statsig';

export { SEGMENT_EVENTS, STATSIG_EVENTS };

const analytics = new AnalyticsBrowser();

const isAdmin = Cookies.get('cb-admin-token');
let ENABLE_TRACKING =
  [ENVIRONMENTS.PRODUCTION, ENVIRONMENTS.STAGING].includes(ENV.CLEARBANC_ENV) &&
  !isDevelopment &&
  !isAdmin;
if (ENV.TEST_SEGMENT) ENABLE_TRACKING = true;
// detect the pre-render so we dont include a bunch of 3rd party scripts
if (navigator.userAgent.indexOf('Headless') > 0) ENABLE_TRACKING = false;

export function loadAnalytics(enabled: boolean, testing = false) {
  if (!enabled) {
    if (!testing) {
      console.log('[ANALYTICS] Tracking disabled - not loading script');
    }
    return false;
  }

  try {
    analytics.load({ writeKey: ENV.SEGMENT_WRITE_KEY });
    return true;
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
}

function callIfFunction(fn?: () => void) {
  if (typeof fn === 'function') fn();
}

// writing a wrapper around analytics.js so we can intercept and mess with
// options and destinations.
// I copied the "Argument Shuffling" sections from their code
const cbAnalytics = {
  analyticsLoaded: loadAnalytics(ENABLE_TRACKING, isTesting),
  async identify(
    id: string,
    traits?: Record<string, any>,
    options?: any,
    fn?: () => void,
  ) {
    try {
      // dont want to default traits to {} as it can reset segment
      if (this.analyticsLoaded) {
        const currentUser = await analytics.user();
        const existingTraits = currentUser.traits() || {};
        const email = (traits || {}).email || existingTraits.email;

        /**
         * FullStory (FS) is integrated through Google Tag Manager (GTM)
         * which is integrated through Segment which attaches window.FS
         * object once loaded
         */
        // @ts-ignore
        if (window.FS) window.FS.identify(id);

        // if we have an email address but no user ID yet
        if (!id && email && !currentUser.id()) {
          currentUser.id(email);
          await analytics.identify(email, traits, {});
          currentUser.id(null);
        } else {
          await analytics.identify(id, traits, options || {}, fn);
        }
      } else {
        if (!isTesting) {
          console.log(
            `[SEGMENT] identify | ID=${id || 'ANONYMOUS'}`,
            traits,
            options || {},
          );
        }
        callIfFunction(fn);
      }
    } catch (error) {
      Sentry.captureMessage(
        `Something went wrong with analytics library (segment) identify ${error}`,
        'warning',
      );
    }
  },
  async track(
    event: SEGMENT_EVENTS,
    properties?: any,
    options?: any,
    fn?: () => void,
  ) {
    try {
      const newProperties = properties || {};

      if (this.analyticsLoaded) {
        const currentUser = await analytics.user();
        const existingTraits = currentUser.traits();

        // we will send FB Standard events seperately
        if (newProperties.fb) {
          const fbProps = newProperties.fb === true ? {} : newProperties.fb;
          delete newProperties.fb;

          // default fb standard event type = ViewContent
          const fbStandardEventType = fbProps.event || 'ViewContent';
          // default content_name = original event name (for ViewContent only)
          let fbContentName = fbProps.content_name;
          if (fbStandardEventType === 'ViewContent')
            fbContentName = fbContentName || event;
          await analytics.track(
            fbStandardEventType,
            {
              ...(fbContentName && { content_name: fbContentName }),
              ...newProperties,
            },
            {
              ...options,
              integrations: { All: false, 'Facebook Pixel': true },
            },
          );
          // you can toggle on the event to only track to facebook
          if (fbProps.only) return;
        }

        // do not track custom events to facebook
        const newOptions = {
          ...options,
          integrations: {
            ...options?.integrations,
            'Facebook Pixel': false,
          },
        };

        // if we have an email address but no user ID yet
        if (existingTraits?.email && !currentUser.id()) {
          currentUser.id(existingTraits.email);
          await analytics.track(event, newProperties, {});
          currentUser.id(null);
        } else {
          await analytics.track(event, newProperties, newOptions, fn);
        }
      } else {
        if (!isTesting) {
          console.log(
            `[SEGMENT] track | EVENT=${event}`,
            newProperties,
            options,
          );
        }
        callIfFunction(fn);
      }
    } catch (error) {
      Sentry.captureMessage(
        `Something went wrong with analytics library (segment) track ${error}`,
        'warning',
      );
    }
  },
  /**
   * Tracks an event for a primary user.
   *
   * This method temporarily switches the analytics identity to the primary user, tracks the event, then switches back
   * to the original user.
   */
  async trackPrimaryUser(
    event: SEGMENT_EVENTS,
    primaryUser: UserResponseDto,
    properties?: any,
    options?: any,
    fn?: () => void,
  ) {
    try {
      const { id: primaryUserId, email } = primaryUser;

      const currentUser = await analytics.user();
      const currentUserId = currentUser.id()?.toString() ?? '';
      const currentTraits = currentUser.traits();

      if (primaryUserId === currentUserId) {
        return await this.track(event, properties, options, fn);
      }

      await this.identify(primaryUserId, {
        email,
      });
      await this.track(
        event,
        {
          properties,
          actualUserId: currentUserId,
        },
        options,
        fn,
      );
      await this.identify(currentUserId, currentTraits);
    } catch (error) {
      Sentry.captureMessage(
        `Something went wrong with analytics library (segment) trackPrimaryUser ${error}`,
        'warning',
      );
    }
  },
  async page(name: string, properties?: any, options?: any, fn?: () => void) {
    try {
      if (this.analyticsLoaded) {
        const currentUser = await analytics.user();
        const existingTraits = currentUser.traits();

        // if we have an email address but no user ID yet
        if (existingTraits?.email && !currentUser.id()) {
          currentUser.id(existingTraits.email);
          await analytics.page(null, name, properties, {});
          currentUser.id(null);
        } else {
          const newOptions = {
            ...options,
            integrations: { ...options?.integrations, Salesforce: false },
          };
          try {
            if (properties.path.includes('/partners/')) {
              const array = properties.path.split('/');
              const index = array.indexOf('partners');
              // eslint-disable-next-line no-param-reassign
              properties.partner = array[index + 1];
              // eslint-disable-next-line no-param-reassign
              properties.source = properties.fullPath?.includes('is_referral')
                ? 'Landing page'
                : 'iFrame';
            }
          } catch (_error) {
            // if error it means extra attributes were not present on parameters
          }
          await analytics.page(null, name, properties, newOptions, fn);
        }
      } else {
        console.log(`[SEGMENT] page | NAME=${name}`, properties, options);
        callIfFunction(fn);
      }
    } catch (error) {
      Sentry.captureMessage(
        `Something went wrong with analytics library (segment) page ${error}`,
        'warning',
      );
    }
  },
  async reset() {
    try {
      if (this.analyticsLoaded) {
        await analytics.reset();
      } else {
        console.log('[SEGMENT] reset');
      }
    } catch (error) {
      Sentry.captureMessage(
        `Something went wrong with analytics library (segment) reset ${error}`,
        'warning',
      );
    }
  },
};

/**
 * Utility function to track business profile step completion and check if all steps are complete
 * This should be called after successful submission of any business profile step
 * @param businessProfileSteps - Object containing business profile steps with their completion status
 */
export function trackBusinessProfileStepCompletion(
  businessProfileSteps: Record<string, { isComplete: boolean }>,
) {
  const completedSteps = Object.keys(businessProfileSteps).filter(
    (step) => businessProfileSteps[step].isComplete,
  ).length;

  const totalSteps = Object.keys(businessProfileSteps).length;

  // Check if all steps are complete, using -1 because it doesn't account for this step that was just completed
  const completingFinalStep = completedSteps === totalSteps - 1;

  if (completingFinalStep) {
    logEvent(STATSIG_EVENTS.business_profile_complete);
  }
}

export default cbAnalytics;
